import React from 'react';
import { useTranslate, ReferenceField, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { H2, TextField, ChipField } from '~/components';
import { TextField as RaTextField, DeleteWithConfirmButton } from '~/components/ra';
import { Section } from '~/layout';
import { useDiffClassFor, useDiffOutlineClassFor } from '~/hooks/useDraftDiffContext';
import EditActions from '~/components/EditActions';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import { Longdash } from '~/utils';
import { Pricing } from '~/types';
import { l } from '~/resources/pricings/PricingShow';
import PricingEdit from '~/resources/pricings/components/PricingEdit';
import { useEditMode } from '~/hooks/useEditModeContext';

interface Props {
  record?: Pricing;
  loading?: boolean;
  basePath?: string;
  readOnly?: boolean;
}

const Settings = (props: Props) => {
  const { record, loading, basePath, readOnly } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const { permissions } = usePermissions();
  const { data: handbook } = useHandbooksQuery(record?.solutionId);
  const diffClass = useDiffClassFor();
  const tagOutline = useDiffOutlineClassFor();
  const kycLevelName = handbook?.kycLevels?.find((level) => level.id === record?.kycLevel)?.name;

  const { editMode } = useEditMode();

  const menuItems = [
    permissions?.includes('solution.pricing.delete') && (
      <DeleteWithConfirmButton key='delete-pricing' record={record} resource='pricings' />
    ),
  ].filter(Boolean);

  return (
    <Section noPaddings>
      <Section.Heading
        bottomSpacing={2}
        actions={
          readOnly ? undefined : editMode === 'DRAFT' ? (
            <PricingEdit record={record} />
          ) : editMode === 'VIEW' ? (
            <EditActions menuItems={menuItems} />
          ) : undefined
        }
      >
        <H2 topSpacing={0} bottomSpacing={0} loading={loading}>
          {t('pricingDetails')}
        </H2>
      </Section.Heading>
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label={t('pricingName')} loading={loading} className={diffClass('name')}>
              {record?.name}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {record?.productId ? (
              <ReferenceField
                source='productId'
                record={record}
                basePath={basePath}
                reference='products'
                link={false}
              >
                <RaTextField
                  source='name'
                  label={t('paymentInstrument')}
                  className={diffClass('productId')}
                />
              </ReferenceField>
            ) : (
              <TextField
                label={t('paymentInstrument')}
                loading={loading}
                className={diffClass('externalSourceType')}
              >
                {record?.externalSourceType}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label={t('activeFrom')}
              loading={loading}
              className={diffClass('activeFrom')}
            >
              {record?.activeFrom && moment(record.activeFrom).format('LL')}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label={t('activeTill')}
              loading={loading}
              className={diffClass('activeTill')}
            >
              {record?.activeTill && moment(record.activeTill).format('LL')}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label={t('profileKycLevel')}
              loading={loading}
              className={diffClass('kycLevel')}
            >
              {kycLevelName}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profileTags')}
              loading={loading}
              textWrap
              className={diffClass('profileTags')}
            >
              {!isEmpty(record?.profileTags)
                ? record?.profileTags?.map((name, index) => (
                    <ChipField
                      label={name}
                      key={index}
                      className={tagOutline(`profileTags[${index}]`)}
                    />
                  ))
                : Longdash}
            </TextField>
          </Grid>
        </Grid>
      </Section>
    </Section>
  );
};

export default Settings;
